import {assert} from 'assert-ts';
import type {
  AndCondition,
  AndConditionV0,
  Condition,
  NotCondition,
  NotConditionV0,
  OrCondition,
  OrConditionV0,
} from '../types';
import type {EvaluationArgs} from './types';
import {evaluateBooleanCondition} from './evaluateBooleanCondition';
import {evaluateExpressionCondition} from './evaluateExpressionCondition';
import {evaluateIncludesCondition} from './evaluateIncludesCondition';
import {evaluateLengthCondition} from './evaluateLengthCondition';
import {evaluateRangeCondition} from './evaluateRangeCondition';
import {evaluateRegexCondition} from './evaluateRegexCondition';
import {
  isAndCondition,
  isAndConditionV0,
  isBooleanCondition,
  isComparisonCondition,
  isComparisonConditionV0,
  isIncludesCondition,
  isLengthCondition,
  isNotCondition,
  isNotConditionV0,
  isOrCondition,
  isOrConditionV0,
  isRangeCondition,
  isRegexCondition,
} from './isConditionType';

export const evaluateNotCondition = (
  condition: NotConditionV0 | NotCondition,
  args: EvaluationArgs,
): boolean => {
  const notArgs =
    (condition as NotCondition).$not ?? (condition as NotConditionV0).arg;

  return !evaluateCondition(notArgs, args);
};

export const evaluateOrCondition = (
  condition: OrConditionV0 | OrCondition,
  args: EvaluationArgs,
): boolean => {
  const arg =
    (condition as OrCondition).$or ?? (condition as OrConditionV0).arg;

  return arg.some(cond => evaluateCondition(cond, args));
};

export const evaluateAndCondition = (
  condition: AndConditionV0 | AndCondition,
  args: EvaluationArgs,
): boolean => {
  const arg =
    (condition as AndCondition).$and ?? (condition as AndConditionV0).arg;

  return arg.every(cond => evaluateCondition(cond, args));
};

export const evaluateCondition = (
  condition: Condition | Condition[],
  args: EvaluationArgs,
): boolean => {
  // Array?
  if (Array.isArray(condition)) {
    return condition.reduce<boolean>(
      (acc, c) => acc && evaluateCondition(c, args),
      true,
    );
  }

  if (isRangeCondition(condition)) {
    return evaluateRangeCondition(condition, args);
  }

  if (isLengthCondition(condition)) {
    return evaluateLengthCondition(condition, args);
  }

  if (isRegexCondition(condition)) {
    return evaluateRegexCondition(condition, args);
  }

  if (isComparisonCondition(condition) || isComparisonConditionV0(condition)) {
    return evaluateExpressionCondition(condition, args);
  }

  if (isNotCondition(condition) || isNotConditionV0(condition)) {
    return evaluateNotCondition(condition, args);
  }

  if (isOrCondition(condition) || isOrConditionV0(condition)) {
    return evaluateOrCondition(condition, args);
  }

  if (isAndCondition(condition) || isAndConditionV0(condition)) {
    return evaluateAndCondition(condition, args);
  }

  if (isBooleanCondition(condition)) {
    return evaluateBooleanCondition(condition, args);
  }

  if (isIncludesCondition(condition)) {
    return evaluateIncludesCondition(condition, args);
  }

  assert.soft(false, 'evaluateCondition: unknown condition', {condition});

  return false;
};
